package com.flowyride.core.domain.favorite

import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Route
import com.flowyride.core.domain.model.RoutePreferences

/** Where a saved ride comes from: planned in FlowyRide, or imported from a GPX file (track kept as is). */
enum class FavoriteSource { PLANNED, IMPORTED }

/** A named stop of a saved ride (departure, stops, arrival; or the waypoints of an imported file). */
data class FavoriteStop(val name: String, val point: GeoPoint)

/** What the favorites list shows: no track, cheap to load. */
data class FavoriteSummary(
    val id: Long,
    val name: String,
    val source: FavoriteSource,
    val distanceMeters: Double,
    val durationSeconds: Double,
    val createdAtEpochMillis: Long,
)

/** A saved ride, route included: reopening it costs no engine call. */
data class Favorite(
    val summary: FavoriteSummary,
    val stops: List<FavoriteStop>,
    val route: Route,
    /** Null for imported rides. */
    val preferences: RoutePreferences?,
)

data class NewFavorite(
    val name: String,
    val source: FavoriteSource,
    val stops: List<FavoriteStop>,
    val route: Route,
    val preferences: RoutePreferences?,
)

/** Failures of favorite operations, mapped to messages by the UI. */
sealed class FavoriteException(message: String, cause: Throwable? = null) : Exception(message, cause) {
    class NotFound : FavoriteException("Favorite not found")
    class Invalid(details: String) : FavoriteException(details)
    class Unreachable(cause: Throwable?) : FavoriteException("Server unreachable: ${cause?.message}", cause)
}

/** Validation rules shared by the apps and the server (authoritative). */
object FavoriteRules {
    const val NAME_MAX = 80
    const val MAX_STOPS = 500

    /** A long GPX recording: far more than any planned ride. */
    const val MAX_TRACK_POINTS = 100_000

    /** @return the first broken rule, or null when [favorite] can be saved. */
    fun validate(favorite: NewFavorite): String? = when {
        favorite.name.isBlank() -> "Name required"
        favorite.name.length > NAME_MAX -> "Name: at most $NAME_MAX characters"
        favorite.route.track.size < 2 -> "Track: at least 2 points"
        favorite.route.track.size > MAX_TRACK_POINTS -> "Track: at most $MAX_TRACK_POINTS points"
        favorite.stops.size > MAX_STOPS -> "At most $MAX_STOPS stops"
        else -> null
    }
}

/** Rides saved on the rider's account. */
interface FavoriteRepository {
    /** Newest first. @throws FavoriteException */
    suspend fun list(): List<FavoriteSummary>

    /** @throws FavoriteException */
    suspend fun get(id: Long): Favorite

    /** @throws FavoriteException */
    suspend fun save(favorite: NewFavorite): FavoriteSummary

    /** @throws FavoriteException */
    suspend fun rename(id: Long, name: String): FavoriteSummary

    /** @throws FavoriteException */
    suspend fun delete(id: Long)
}
